import { Howl, Howler } from 'howler';
import { howlerStore } from './howlerStore.js';
// import track from './store/modules/track.js';
import { mapActions, mapState } from 'vuex';

export const HowlerPlugin = {
    install: (app, options) => {
        Howler.autoUnlock = true;
        Howler.html5PoolSize = 100;
        Howler.autoSuspend = false;

        app.mixin({
            data: () => ({
                howlerTracks: howlerStore.howlerTracks,
                howlerSceneTracks: howlerStore.howlerSceneTracks,
                howlerLibraryTracks: howlerStore.howlerLibraryTracks,
            }),

            computed: {
                ...mapState('partyRoom', ['partyRoomChannel']),

                // Get all tracks from howlerSceneTracks and howlerTracks that has action 'play'
                howlerPlayingTracks() {
                    return Object.values(this.howlerSceneTracks).flat().filter(track => track.action === 'play')
                        .concat(Object.values(this.howlerTracks).flat().filter(track => track.action === 'play'));
                }
            },

            methods: {
                // Create a new Howl instance for a track, and if scene_id is provided, attach it to the right scene
                async howlerInitTrack(track, instantLoad = false) {
                    console.log('howlerInitTrack', track, instantLoad);
                    if (track.audioTrack) {
                        // If track already has an audioTrack, return it
                        return new Promise((resolve) => {
                            resolve(track);
                        });
                    }

                    return new Promise((resolve, reject) => {
                        console.log('Initializing track', track);
                        // const apiUrl = import.meta.env.VITE_API_DOMAIN;
                        let trackSource = 'track/' + track.source;

                        if (track.album_id) {
                            console.log('is album track');
                            trackSource = trackSource + '?library';
                        }

                        console.log('trackSource', trackSource);

                        var audioTrack = new Howl({
                            src: [trackSource],
                            html5: true,
                            preload: instantLoad ? false : 'metadata',
                            autoplay: false,
                            loop: track.pivot?.loop || false,
                            xhr: {
                                method: 'GET',
                                withCredentials: true
                            },

                            onloaderror (id, error) {
                                console.error('Error loading track', error);
                            },

                            onload: () => {
                                // console.log('onLoad');
                            },

                            onplay: () => {
                                console.log('onPlay', track.id);
                                if (track.pivot) {
                                    track.fadeInTimer = track.pivot.fade_in_timer || 0;
                                    track.fadeOutTimer = track.pivot.fade_out_timer || 0;

                                    track.animationFrame = requestAnimationFrame(() => {
                                        this.howlerTrackUpdateLoop(track);
                                    });
                                } else {
                                    track.animationFrame = requestAnimationFrame(() => {
                                        this.howlerTrackUpdateLoop(track);
                                    });
                                }

                                if (track.currentTime >= 1) {
                                    track.audioTrack.seek(track.currentTime);
                                }

                                track.action = 'play';
                            },

                            onpause: () => {
                                console.log('onPause', track.id);
                                cancelAnimationFrame(track.animationFrame);
                                track.fadeInProgress = false;
                                track.action = 'pause';
                            },

                            onstop: () => {
                                console.log('onStop', track.id);
                                cancelAnimationFrame(track.animationFrame);
                                track.fadeInProgress = false;
                                track.currentTime = 0;
                                track.action = 'stop';
                            },

                            onend: () => {
                                console.log('onEnd', track.id);
                                // if (!track.audioTrack.loop()) {
                                //     this.howlerStopTrack(track);
                                //     track.fadeInProgress = false;
                                //     cancelAnimationFrame(track.animationFrame);
                                // }
                                track.currentTime = 0;
                                track.action = 'stop';
                                if (track.pivot) {
                                    track.pivot.current_time = 0;
                                }
                            },
                        });

                        if (track.pivot && track.pivot.volume) {
                            audioTrack.volume(track.pivot?.volume / 100 || 1.0);
                        } else {
                            audioTrack.volume(track.volume / 100 || 1.0);
                        }

                        if (track.pivot && track.pivot.current_time) {
                            audioTrack.seek(track.pivot.current_time);
                            track.currentTime = track.pivot.current_time;
                        } else {
                            track.currentTime = 0;
                        }

                        track.audioTrack = audioTrack;

                        resolve(track);
                    });
                },

                howlerGetSceneTracks(scene_id) {
                    // Check if scene exists in the sceneTracks object
                    if (this.howlerSceneTracks[scene_id] === undefined) {
                        return [];
                    }

                    return this.howlerSceneTracks[scene_id];
                },

                howlerGetTrack(track_id, scene_id) {
                    return new Promise((resolve, reject) => {
                        if (scene_id) {
                            if (this.howlerSceneTracks[scene_id] === undefined) {
                                this.howlerSceneTracks[scene_id] = [];
                            }

                            const sceneTracksArray = this.howlerSceneTracks[scene_id];

                            // For some reason we had to add a timeout here, otherwise the track might not be found immediately
                            setTimeout(() => {
                                resolve(sceneTracksArray.find(track => track.id === track_id));
                            }, 10);
                        } else {
                            resolve(this.howlerTracks.find(track => track.id === track_id));
                        }
                    });
                },

                async howlerGetLibraryTrack(trackId) {
                    return new Promise((resolve, reject) => {
                        resolve(this.howlerLibraryTracks.find(t => t.id === trackId));
                    });
                },

                // Add tracks to the store, optionally by scene_id
                howlerAddTrack(data) {
                    if (data.scene_id) {
                        if (this.howlerSceneTracks[data.scene_id] === undefined) {
                            this.howlerSceneTracks[data.scene_id] = [];
                        } else {
                            // Prevent duplicates
                            const trackIndex = this.howlerSceneTracks[data.scene_id].findIndex(track => track.id === data.track.id);

                            if (trackIndex !== -1) {
                                // Track already exists in the scene, do not add it again
                                return;
                            }
                        }

                        // Add track.scenes if it doesn't exist
                        if (!data.track.scenes) {
                            data.track.scenes = [];
                        }

                        // Add scene to the track.scenes array if it doesn't exist
                        if (!data.track.scenes.find(scene => scene.id === data.scene_id)) {
                            data.track.scenes.push({
                                id: data.scene_id,
                                pivot: {
                                    current_time: 0,
                                    fade_in_timer: data.track.fade_in_timer || 0,
                                    fade_out_timer: data.track.fade_out_timer || 0,
                                    volume: data.track.volume || 50,
                                    interval_countdown: 0,
                                    loop: 0,
                                    status: 1,
                                    track_id: data.track.id,
                                    scene_id: data.scene_id
                                }
                            });
                        }

                        this.howlerSceneTracks[data.scene_id].push(data.track);
                    } else if (data.track.album_id) {
                        if (this.howlerLibraryTracks.find(track => track.id === data.track.id)) {
                            return;
                        }

                        this.howlerLibraryTracks.push(data.track);
                    } else {
                        // Check if track already exists in the tracks array
                        if (this.howlerTracks.find(track => track.id === data.track.id)) {
                            return;
                        }

                        this.howlerTracks.push(data.track);
                    }

                    // console.log('howlerAddTrack', data.track);
                },

                // Remove track from the store, optionally by sceneId
                howlerRemoveTrack(trackId, sceneId = null) {
                    if (sceneId) {
                        if (this.howlerSceneTracks[sceneId] === undefined) {
                            return;
                        }

                        const sceneTracksArray = this.howlerSceneTracks[sceneId];
                        const trackIndex = sceneTracksArray.findIndex(track => track.id === trackId);

                        if (trackIndex !== -1) {
                            sceneTracksArray.splice(trackIndex, 1);
                        }

                        // Remove scene from the track.scenes array
                        const track = this.howlerTracks.find(track => track.id === trackId);
                        if (track) {
                            const sceneIndex = track.scenes.findIndex(scene => scene.id === sceneId);
                            if (sceneIndex !== -1) {
                                track.scenes.splice(sceneIndex, 1);
                            }
                        }
                    } else {
                        const trackIndex = this.howlerTracks.findIndex(track => track.id === trackId);

                        if (trackIndex !== -1) {
                            this.howlerTracks.splice(trackIndex, 1);
                        }
                    }
                },

                howlerToggleActiveTrack(track, sceneId) {
                    let sceneTracks = this.howlerSceneTracks[sceneId];
                    if (!sceneTracks) {
                        console.error('Scene not found for track toggle');
                        return;
                    }

                    // Toggle the track's active status
                    if (sceneTracks.find(t => t.id === track.id)) {
                        if (track.pivot?.status === 1) {
                            track.pivot.status = 0;
                        } else {
                            track.pivot.status = 1;
                        }
                    }
                },

                // Remove audioTrack from all howlerTracks
                howlerClearTracks() {
                    console.log('Clearing all tracks');
                    this.howlerTracks.forEach(track => {
                        if (track.audioTrack) {
                            track.audioTrack.unload();
                        }
                    });
                },

                async howlerPlayTrack(track, seekTime = null) {
                    if (!track) {
                        return;
                    }

                    console.log('Howler play track', track, seekTime);
                    if (!track.audioTrack) {
                        console.log('Track audioTrack not initialized, initializing now');
                        await this.howlerInitTrack(track, true);
                    }

                    // console.log('Track current Time', track.currentTime, seekTime, track.audioTrack.seek());
                    track.audioTrack.seek(seekTime !== null ? seekTime : (track.currentTime || 0));
                    // If currentTime is close enough to end of the track, reset to 0 (within last second)
                    if (track.duration - (seekTime !== null ? seekTime : (track.currentTime || 0)) < 1) {
                        // console.log('Seek time close to end of track, resetting to 0', track.duration, (seekTime !== null ? seekTime : (track.currentTime || 0)));
                        track.audioTrack.seek(0);
                    }
                    // console.log('Track audioTrack currentTime', track.currentTime, seekTime, track.audioTrack.seek());
                    track.action = 'play';
                    track.howlerId = track.audioTrack.play();

                    this.howlerBroadcastTrackAction(track);
                },

                howlerPauseTrack(track) {
                    if (!track) {
                        return;
                    }

                    console.log('Howler pause track');
                    if (!track.audioTrack) {
                        // console.log('Track audioTrack not initialized, initializing now');
                        this.howlerInitTrack(track, true);
                    }

                    track.audioTrack.pause();
                    track.audioTrack.loop(false);
                    track.action = 'pause';

                    this.howlerBroadcastTrackAction(track);
                },

                howlerStopTrack(track) {
                    if (!track) {
                        return;
                    }
                    // console.log('Howler stop track', track.id);
                    if (!track.audioTrack) {
                        this.howlerInitTrack(track);
                    }
                    track.audioTrack.stop();
                    track.currentTime = 0;
                    track.audioTrack.loop(false);
                    track.action = 'stop';
                    if (track.pivot) {
                        track.pivot.current_time = 0;
                    }

                    this.howlerBroadcastTrackAction(track);
                },

                howlerPlayScene(scene) {
                    const sceneId = scene.id;
                    const sceneTracksArray = this.howlerSceneTracks[sceneId];

                    // Check for scene settings, so we can play the scene accordingly
                    // If stop_everything_else_on_play is enabled, we stop all other scenes
                    if (scene.settings?.stop_everything_else_on_play === 1) {
                        console.log('Stop everything else enabled');
                        Object.keys(this.howlerSceneTracks).forEach(key => {
                            // console.log('Checking key', key, sceneId);
                            if (Number.parseInt(key) !== Number.parseInt(sceneId)) {
                                this.howlerPauseScene(key);
                            }
                        });
                    }

                    // If scene type is 2 (Play one random track)
                    if (scene.type === 2) {
                        console.log('Play one random track enabled');
                        const tracks = sceneTracksArray.filter(track => track.pivot?.status === 1);

                        if (tracks.length === 0) {
                            return;
                        }

                        var trackRng = Math.floor(Math.random() * tracks.length);
                        var rngTrack = tracks[trackRng];

                        if (rngTrack) {
                            // When playing random track, set current time to 0 on the track, so we always start from the beginning
                            this.howlerPlayTrack(rngTrack, 0);
                            // rngTrack.audioTrack.seek(0);
                            // Add on stop event to start the scene again if we have endless play enabled
                            if (scene.settings?.endless_play === 1) {
                                console.log('Endless play enabled');
                                rngTrack.audioTrack.once('end', () => {
                                    console.log('Endless play on end');
                                    this.howlerPlayScene(scene);
                                });
                            }
                        }

                        return;
                    }

                    // If scene type is 3 (Play all tracks in order)
                    if (scene.type === 3) {
                        console.log('Play all tracks in order enabled');
                        let trackIndex = 0;
                        let activeTracks = sceneTracksArray.filter(track => track.pivot?.status === 1);

                        if (activeTracks.length === 0) {
                            return;
                        }

                        const playNextTrack = () => {
                            if (trackIndex >= activeTracks.length) {
                                trackIndex = 0;
                            }

                            this.howlerPlayTrack(activeTracks[trackIndex]);
                            activeTracks[trackIndex].audioTrack.once('end', () => {
                                trackIndex++;
                                playNextTrack();
                            });
                        }

                        playNextTrack();

                        return;
                    }

                    sceneTracksArray.forEach(track => {
                        if (track.pivot?.status === 0) {
                            return;
                        }

                        if (!track.audioTrack) {
                            this.howlerInitTrack(track, true);
                        }

                        this.howlerPlayTrack(track);
                    });
                },

                howlerPauseScene(sceneId) {
                    // If arg is object, take id from object
                    if (typeof sceneId === 'object') {
                        sceneId = sceneId.id;
                    }

                    const sceneTracksArray = this.howlerSceneTracks[sceneId].filter(track => track.audioTrack);

                    sceneTracksArray.forEach(track => {
                        if (track.pivot?.status === 0 || !track.audioTrack.playing()) {
                            return;
                        }

                        // track.audioTrack.pause();
                        this.howlerPauseTrack(track);
                    });
                },

                howlerStopScene(sceneId) {
                    // If arg is object, take id from object
                    if (typeof sceneId === 'object') {
                        sceneId = sceneId.id;
                    }

                    // console.log('Stopping scene', sceneId);
                    const sceneTracksArray = this.howlerSceneTracks[sceneId];

                    sceneTracksArray.forEach(track => {
                        if (track.pivot?.status === 0 || track.audioTrack.seek() === 0) {
                            return;
                        }

                        // track.audioTrack.stop();
                        this.howlerStopTrack(track);
                    });
                },

                howlerStopAllLibraryTracks() {
                    this.howlerLibraryTracks.forEach(track => {
                        if (track.audioTrack && track.audioTrack.playing()) {
                            this.howlerStopTrack(track);
                        }
                    });
                },

                // Set global Howler volume (0.0 to 1.0)
                howlerSetGlobalVolume(volume) {
                    Howler.volume(volume);
                },

                // Toggle loop for a track
                howlerToggleLoop(track) {
                    if (!track.audioTrack) {
                        this.howlerInitTrack(track);
                    }
                    track.audioTrack.loop(!track.audioTrack.loop());
                    if (!track.pivot) {
                        track.pivot = {
                            loop: track.audioTrack.loop() ? 1 : 0
                        };
                    }

                    track.pivot.loop = track.audioTrack.loop() ? 1 : 0;
                },

                howlerSetVolume(track, volume, createPivot) {
                    // console.log('howlerSetVolume', track, volume);
                    if (!track.audioTrack) {
                        this.howlerInitTrack(track);
                    }

                    track.audioTrack.volume(volume / 100);
                    if (!track.pivot && createPivot) {
                        track.pivot = {
                            volume: volume
                        };
                    }

                    if (track.pivot) {
                        track.pivot.volume = volume;
                    }

                    // Debounce volume changes so we don't send too many requests
                    clearTimeout(track.volumeTimeout);
                    track.volumeTimeout = setTimeout(() => {
                        this.howlerBroadcastTrackAction(track, 'volume');
                    }, 500);
                },

                howlerTrackUpdateLoop(track) {
                    // console.log('howlerTrackUpdateLoop', track.id, track.currentTime);
                    track.animationFrame = requestAnimationFrame(() => {
                        track.currentTime = track.audioTrack.seek(); // Makes the progress bar work
                        // console.log('howlerTrackUpdateLoop', track.currentTime);

                        if (track.fadeInTimer || track.fadeOutTimer) {
                            // If we are in the fade in period, run fade system
                            if (track.currentTime < track.fadeInTimer || track.currentTime > track.duration - track.fadeOutTimer) {
                                this.runHowlerFadeSystem(track);
                            }
                        }

                        this.howlerTrackUpdateLoop(track);
                    });
                },

                runHowlerFadeSystem(track) {
                    if (track.fadeInProgress) {
                        return;
                    }

                    // Get duration of track
                    let duration = track.duration;

                    // Get current time of track
                    let currentTime = track.currentTime;
                    // console.log('runFadeSystem', currentTime, track.fadeInTimer, track.fadeOutTimer, duration);

                    if (track.fadeInTimer || track.fadeOutTimer) {
                        // If we are in the fade in period, run fade system
                        if (track.fadeInTimer > 0) {
                            // If we are in the fade in period, run fade system
                            if (currentTime < track.fadeInTimer) {
                                track.fadeInProgress = true;

                                // Calculate fade in values, fadeInPercentage should be 0 if fadeIn just started
                                let fadeInPercentage = parseInt((currentTime / track.fadeInTimer) * 100);
                                // Calculate fade in duration left, in the beginning it should be the full fade in time, but decrease as time goes on
                                let fadeDurationLeft = (track.fadeInTimer - currentTime) * 1000;

                                // Figure what the volume should be, between 0 and set volume
                                let volume = parseFloat((track.pivot.volume / 100) * (fadeInPercentage / 100));

                                // console.log('fade in', volume, parseFloat(track.pivot.volume / 100), fadeDurationLeft, track.fadeInProgress, fadeInPercentage + '%');

                                // Set fade (from, to, duration)
                                track.audioTrack.fade(volume, parseFloat(track.pivot.volume / 100), fadeDurationLeft, track.howlerId);

                                // Set timer to remove fade in progress after fade in period
                                setTimeout(() => {
                                    track.fadeInProgress = false;
                                }, fadeDurationLeft + 100);
                            }
                        }

                        if (track.fadeOutTimer > 0) {
                            // If we are in the fade out period, run fade system
                            if (currentTime > duration - track.fadeOutTimer) {
                                track.fadeInProgress = true;

                                // Calculate fade out values, fadeOutPercentage should be 0 if fadeOut just started
                                let fadeOutPercentage = parseInt(100 - (((duration - currentTime) / track.fadeOutTimer) * 100));
                                // Calculate fade out duration left, in the beginning it should be the full fade out time, but decrease as time goes on
                                let fadeDurationLeft = (track.fadeOutTimer * 1000) - ((currentTime - (duration - track.fadeOutTimer)) * 1000);

                                // Figure what the volume should be, between 0 and set volume
                                let volume = parseFloat(track.pivot.volume * ((100 - fadeOutPercentage) / 100) / 100);

                                // console.log('fade out', volume, 0, fadeDurationLeft, fadeOutPercentage);

                                // Set fade (from, to, duration)
                                track.audioTrack.fade(volume, 0, fadeDurationLeft, track.howlerId);

                                // Set timer to remove fade in progress after fade out period
                                setTimeout(() => {
                                    track.fadeInProgress = false;
                                }, fadeDurationLeft + 100);
                            }
                        }
                    }
                },

                ...mapActions('partyRoom', ['broadcastTrackAction']),

                // If we have broadcast channel, send request to broadcast track action
                howlerBroadcastTrackAction(data, action = null) {
                    // If playing from Album, do not broadcast
                    if (data.album_id) {
                        return;
                    }

                    // console.log('Broadcasting track action', this.partyRoomChannel, data);
                    if (this.partyRoomChannel) {
                        console.log('Sending broadcast track action to:', this.partyRoomChannel);

                        let volume = 50;

                        if (data.pivot && data.pivot.volume >= 0) {
                            volume = parseInt(data.pivot.volume);
                        } else {
                            volume = parseInt(data.volume);
                        }

                        this.broadcastTrackAction({
                            room_key: this.partyRoomChannel,
                            track_id: data.id,
                            scene_id: data.pivot?.scene_id || null,
                            action: action || data.action,
                            fade_in_timer: data.pivot?.fade_in_timer || 0,
                            fade_out_timer: data.pivot?.fade_out_timer || 0,
                            volume: volume,
                            seek: data.audioTrack ? data.audioTrack.seek() : 0,
                        });
                    }
                },
            }
        });
    }
};

export default HowlerPlugin;
